import React, { CSSProperties } from 'react';

type ChipColor = 'white' | 'red' | 'green' | 'blue' | 'black' | 'gold';

type PokerChipProps = {
  size?: number;
  color?: ChipColor;
  style?: CSSProperties;
};

const chipColors: Record<ChipColor, { bg: string; stripe: string; text: string }> = {
  white: { bg: '#e8e4de', stripe: '#d0ccc6', text: '#666' },
  red:   { bg: '#c0392b', stripe: '#a93226', text: '#fdd' },
  green: { bg: '#27ae60', stripe: '#219a52', text: '#d5f5e3' },
  blue:  { bg: '#2980b9', stripe: '#2471a3', text: '#d4e6f1' },
  black: { bg: '#2c3e50', stripe: '#1a252f', text: '#aab' },
  gold:  { bg: '#d4a017', stripe: '#b8860b', text: '#fff8dc' }
};

/**
 * Pure CSS poker chip with edge stripes and center mark.
 */
const PokerChip = ({ size = 24, color = 'red', style }: PokerChipProps) => {
  const c = chipColors[color];
  const stripeWidth = size * 0.12;
  const innerSize = size * 0.62;
  const fontSize = size * 0.32;

  const outerStyle: CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    background: `
      repeating-conic-gradient(
        ${c.stripe} 0deg 15deg,
        ${c.bg} 15deg 30deg
      )
    `,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 1px 3px rgba(0,0,0,0.3), inset 0 0 0 ${stripeWidth}px ${c.bg}`,
    position: 'relative',
    flexShrink: 0,
    ...style
  };

  const innerStyle: CSSProperties = {
    width: innerSize,
    height: innerSize,
    borderRadius: '50%',
    backgroundColor: c.bg,
    border: `1.5px dashed ${c.stripe}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: fontSize,
    fontWeight: 800,
    color: c.text,
    fontFamily: "'Outfit', sans-serif",
    lineHeight: 1,
    textShadow: '0 0.5px 1px rgba(0,0,0,0.15)'
  };

  return (
    <div style={outerStyle}>
      <div style={innerStyle}>H</div>
    </div>
  );
};

export default PokerChip;

/**
 * Get chip color based on pot value tier.
 */
export const getChipColor = (value: number): ChipColor => {
  if (value >= 100) return 'black';
  if (value >= 50) return 'gold';
  if (value >= 20) return 'green';
  if (value >= 10) return 'blue';
  if (value >= 1) return 'red';
  return 'white';
};
